import React, { forwardRef } from "react";
import { composeAgentBrand } from "@/lib/brandLabel";

/**
 * EstimateReportPage — Report A4 1-pagina della stima OMI.
 *
 * Layout editorial pulito: header brand + città in evidenza + range stima
 * grande + dettaglio aggiustamenti + box confidenza + disclaimer + CTA agente.
 *
 * Renderizzato a misura A4 (210×297mm a 1748×2480px @ 210 DPI) per
 * `exportSinglePageAsPdf` da `lib/staticExport.js`.
 *
 * Props:
 *   - result: response da POST /api/estimate/value
 *   - form: i dati immessi (city, surface_sqm, state, zone, ...)
 *   - agency: { name, phone, email, agent_name, logo_url }
 *   - accentColor: optional, default verde Brera ("#7C8B6E")
 */

const A4_W = 1748; // px @ 210 DPI
const A4_H = 2480;

const _eur = (n) => {
  if (n == null || isNaN(n)) return "—";
  return `€ ${Math.round(n).toLocaleString("it-IT")}`;
};
const _eurMq = (n) => {
  if (n == null || isNaN(n)) return "—";
  return `€ ${Math.round(n).toLocaleString("it-IT")}/m²`;
};

const STATE_LABEL = {
  nuovo: "Nuovo / Ristrutturato",
  ottimo: "Ottimo stato",
  buono: "Buono stato",
  normale: "Normale",
  scarso: "Scarso",
  da_ristrutturare: "Da ristrutturare",
};

const ZONE_LABEL = {
  centrale: "Centrale",
  semicentrale: "Semicentrale",
  periferica: "Periferica",
  extraurbana: "Extraurbana",
};

const TYPE_LABEL = {
  appartamento: "Appartamento",
  villa: "Villa",
  villetta: "Villetta a schiera",
  attico: "Attico",
  loft: "Loft",
  rustico: "Rustico",
  bilocale: "Bilocale",
  trilocale: "Trilocale",
};

const CONFIDENCE_LABEL = {
  alta: { txt: "ALTA", note: "Hai compilato dettagli ricchi." },
  media: { txt: "MEDIA", note: "Dati base completi, range moderato." },
  bassa: { txt: "BASSA", note: "Pochi dati o città non in archivio OMI." },
};

const EstimateReportPage = forwardRef(function EstimateReportPage(
  { result, form, agency = {}, accentColor = "#7C8B6E" },
  ref,
) {
  if (!result) return null;

  const today = new Date().toLocaleDateString("it-IT", {
    day: "2-digit", month: "long", year: "numeric",
  });

  const pricePerSqm = result.price_per_sqm;
  const pricePerSqmMin = result.price_per_sqm_min;
  const pricePerSqmMax = result.price_per_sqm_max;
  const estimated = result.estimated;
  const rangeMin = result.range_min;
  const rangeMax = result.range_max;
  const confidence = (result.confidence || "media").toLowerCase();
  const confInfo = CONFIDENCE_LABEL[confidence] || CONFIDENCE_LABEL.media;

  const omiBase = result.city_omi_data?.median;
  const usedNational = result.used_national_average;
  const neighborhoodResolved = result.neighborhood_resolved;
  const cityKey = result.city_key || "";

  // Aggiustamenti applicati (calcolati dal price/mq vs omi base)
  const totalMultiplier = omiBase ? (pricePerSqm / omiBase) : 1;
  const totalAdjustmentPct = Math.round((totalMultiplier - 1) * 100);

  const agentName = agency.agent_name || agency.name || "Il tuo agente";
  const agencyName = agency.name || "Pronto";
  const agencyPhone = agency.phone || "";
  const agencyEmail = agency.email || "";
  const logoUrl = agency.logo_url || null;

  return (
    <div
      ref={ref}
      data-testid="estimate-report-a4"
      style={{
        width: `${A4_W}px`,
        height: `${A4_H}px`,
        backgroundColor: "#FBFAF6",
        position: "relative",
        fontFamily: "'Manrope', system-ui, -apple-system, sans-serif",
        color: "#1C1C1A",
        boxSizing: "border-box",
        padding: "120px 110px",
        overflow: "hidden",
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "8px",
          backgroundColor: accentColor,
        }}
      />

      {/* Header — agency brand + date */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        borderBottom: "1px solid #EAE7DF", paddingBottom: 36, marginBottom: 56,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt={agencyName}
              crossOrigin="anonymous"
              style={{ height: 90, width: 90, objectFit: "contain", borderRadius: 8, filter: "brightness(0)" }}
            />
          )}
          <div>
            <div style={{
              fontSize: 18, letterSpacing: "0.22em", textTransform: "uppercase",
              fontWeight: 700, color: accentColor, marginBottom: 8,
            }}>
              VALUTAZIONE IMMOBILIARE
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#1C1C1A" }}>
              {agencyName}
            </div>
            <div style={{ fontSize: 18, color: "#6B6A65", marginTop: 6 }}>
              a cura di {agentName}
            </div>
          </div>
        </div>
        <div style={{ textAlign: "right", color: "#6B6A65", fontSize: 16 }}>
          <div style={{
            fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
            fontWeight: 700, color: "#9A968B", marginBottom: 6,
          }}>
            REPORT DEL
          </div>
          <div style={{ fontSize: 22, color: "#1C1C1A", fontWeight: 600 }}>
            {today}
          </div>
        </div>
      </div>

      {/* Title */}
      <div style={{ marginBottom: 60 }}>
        <div style={{
          fontSize: 13, letterSpacing: "0.22em", textTransform: "uppercase",
          fontWeight: 700, color: accentColor, marginBottom: 14,
        }}>
          Stima OMI · {form?.city || cityKey}
        </div>
        <h1 style={{
          fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
          fontSize: 76, fontWeight: 600, lineHeight: 1.05,
          letterSpacing: "-0.01em", margin: 0, color: "#1C1C1A",
        }}>
          Quanto vale<br/>
          <em style={{ color: accentColor, fontStyle: "italic" }}>questo immobile.</em>
        </h1>
        {form?.address && (
          <div style={{ fontSize: 22, color: "#6B6A65", marginTop: 24 }}>
            {form.address}
          </div>
        )}
      </div>

      {/* Range card — the big number */}
      <div style={{
        backgroundColor: "#1C1C1A", color: "#FFFFFF", borderRadius: 24,
        padding: "56px 64px", marginBottom: 56, position: "relative", overflow: "hidden",
      }}>
        <div
          style={{
            position: "absolute", top: -80, right: -80, width: 280, height: 280,
            borderRadius: "50%", background: `radial-gradient(circle, ${accentColor}40 0%, transparent 70%)`,
          }}
        />
        <div style={{
          fontSize: 14, letterSpacing: "0.22em", textTransform: "uppercase",
          fontWeight: 700, color: accentColor, marginBottom: 18,
        }}>
          VALORE STIMATO
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 32, marginBottom: 24 }}>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif", fontSize: 110, fontWeight: 600,
            lineHeight: 1, letterSpacing: "-0.02em",
          }}>
            {_eur(estimated)}
          </div>
          <div style={{ color: "#9A968B", fontSize: 22 }}>
            valore centrale
          </div>
        </div>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingTop: 28, borderTop: "1px solid rgba(255,255,255,0.12)",
        }}>
          <div>
            <div style={{ color: "#9A968B", fontSize: 14, letterSpacing: "0.18em",
                          textTransform: "uppercase", marginBottom: 6 }}>
              Range realistico di mercato
            </div>
            <div style={{ fontSize: 36, fontWeight: 600 }}>
              {_eur(rangeMin)}<span style={{ color: "#9A968B" }}> – </span>{_eur(rangeMax)}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "#9A968B", fontSize: 14, letterSpacing: "0.18em",
                          textTransform: "uppercase", marginBottom: 6 }}>
              Prezzo al metro²
            </div>
            <div style={{ fontSize: 36, fontWeight: 600 }}>{_eurMq(pricePerSqm)}</div>
            <div style={{ fontSize: 16, color: "#9A968B", marginTop: 2 }}>
              {_eurMq(pricePerSqmMin)} – {_eurMq(pricePerSqmMax)}
            </div>
          </div>
        </div>
      </div>

      {/* Detail grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, marginBottom: 50 }}>
        {/* Left — Caratteristiche immobile */}
        <div>
          <div style={{
            fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase",
            fontWeight: 700, color: "#9A968B", marginBottom: 18,
          }}>
            Caratteristiche immobile
          </div>
          {[
            ["Località", form?.city ? `${form.city}${neighborhoodResolved ? ` (zona ${result.city_key})` : ""}` : cityKey],
            ["Tipologia", TYPE_LABEL[form?.property_type] || form?.property_type || "—"],
            ["Superficie", `${form?.surface_sqm || "—"} m²`],
            form?.rooms && ["Locali", `${form.rooms}`],
            ["Stato", STATE_LABEL[form?.state] || form?.state],
            ["Zona", ZONE_LABEL[form?.zone] || form?.zone],
            form?.year_built && ["Anno", `${form.year_built}`],
            form?.floor != null && form?.floor !== "" && ["Piano", `${form.floor}${form?.has_elevator ? " · ascensore" : ""}`],
            form?.energy_class && ["Classe energetica", form.energy_class.toUpperCase()],
            form?.exposure && ["Esposizione", form.exposure],
            form?.view && ["Vista", form.view],
          ].filter(Boolean).map(([label, value], i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between",
              padding: "16px 0", borderBottom: "1px solid #EAE7DF",
              fontSize: 18,
            }}>
              <span style={{ color: "#6B6A65" }}>{label}</span>
              <span style={{ color: "#1C1C1A", fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* Right — Calcolo */}
        <div>
          <div style={{
            fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase",
            fontWeight: 700, color: "#9A968B", marginBottom: 18,
          }}>
            Come abbiamo valutato
          </div>
          {[
            ["Valore OMI base", omiBase ? `${_eurMq(omiBase)}` : "fascia nazionale"],
            ["Aggiustamenti applicati", `${totalAdjustmentPct >= 0 ? "+" : ""}${totalAdjustmentPct}%`],
            ["Prezzo finale al m²", _eurMq(pricePerSqm)],
          ].map(([label, value], i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between",
              padding: "16px 0", borderBottom: "1px solid #EAE7DF",
              fontSize: 18,
            }}>
              <span style={{ color: "#6B6A65" }}>{label}</span>
              <span style={{ color: "#1C1C1A", fontWeight: 600 }}>{value}</span>
            </div>
          ))}

          {/* Confidence badge */}
          <div style={{
            marginTop: 32, padding: 24, borderRadius: 14,
            backgroundColor: "#FFFFFF", border: "1px solid #EAE7DF",
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 10,
            }}>
              <span style={{
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                fontWeight: 700, color: "#9A968B",
              }}>
                Affidabilità della stima
              </span>
              <span style={{
                display: "inline-block", padding: "4px 14px", borderRadius: 100,
                backgroundColor: accentColor, color: "#FFFFFF",
                fontSize: 12, fontWeight: 700, letterSpacing: "0.16em",
              }}>
                {confInfo.txt}
              </span>
            </div>
            <div style={{ fontSize: 16, color: "#3A3A36", lineHeight: 1.4 }}>
              {confInfo.note} {usedNational && "Quotazione basata su mediana nazionale (OMI non disponibile per la città)."}
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer + CTA — pinned to bottom */}
      <div style={{
        position: "absolute", left: 110, right: 110, bottom: 100,
      }}>
        <div style={{
          fontSize: 13, color: "#9A968B", lineHeight: 1.5, marginBottom: 28,
          paddingBottom: 24, borderBottom: "1px solid #EAE7DF",
        }}>
          <strong style={{ color: "#6B6A65" }}>Disclaimer.</strong> Questa è una stima
          indicativa basata sulle quotazioni OMI 2° semestre 2025 dell&apos;Agenzia delle Entrate
          e su algoritmi proprietari di Pronto. Non sostituisce una perizia tecnica
          giurata. Il valore reale dipende da fattori locali (luminosità, finiture,
          stato condominio) valutabili solo con sopralluogo.
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{
              fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase",
              fontWeight: 700, color: accentColor, marginBottom: 8,
            }}>
              VUOI UNA VALUTAZIONE PRECISA?
            </div>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 600,
              color: "#1C1C1A", lineHeight: 1.2, marginBottom: 14,
            }}>
              Prenota un sopralluogo gratuito.
            </div>
            <div style={{ fontSize: 16, color: "#3A3A36" }}>
              {(() => {
                // iter v29 r201 — evita "Luca Ferrari · Casa Bella di Luca Ferrari"
                const { primary, secondary } = composeAgentBrand(agentName, agencyName);
                return secondary ? <>{primary} · {secondary}</> : primary;
              })()}<br />
              {agencyPhone && <>{agencyPhone}<br /></>}
              {agencyEmail && agencyEmail}
            </div>
          </div>
          <div style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 16, fontStyle: "italic", color: "#9A968B",
          }}>
            Pronto · prontoimmo.it
          </div>
        </div>
      </div>
    </div>
  );
});

export default EstimateReportPage;
